/**
 * Visual Component CMS — domain types.
 * Mirrors the SQL surface defined in 20260615120000_visual_cms_module.sql.
 * Kept separate from the existing cms_pages module.
 */

import type { Json } from "@/lib/supabase/types";

export type VisualCmsPageStatus = "draft" | "published";

export const VISUAL_CMS_PAGE_TYPES = [
  "standard",
  "about",
  "faq",
  "trust_safety",
  "contact",
  "community_guidelines",
  // "policy",
  "privacy_policy",
  "terms_of_service",
  "help",
  "how_it_works",
  "homepage",
  // "landing",
  // "custom",
  // ^ Deprecated. The "standard" page type covers all freeform use cases
  //   (any combination of the 12 built section types in any order). The
  //   DB CHECK constraint still permits 'custom' for backward compatibility
  //   with any existing rows; the admin picker hides it and the public
  //   dispatch routes 'custom' pages through DynamicStandardTemplate via
  //   the default case.
] as const;
export type VisualCmsPageType = (typeof VISUAL_CMS_PAGE_TYPES)[number];

export const VISUAL_CMS_SECTION_TYPES = [
  "hero",
  "card_grid",
  "faq",
  "cta_banner",
  "timeline",
  "trust_blocks",
  "guidelines",
  "rich_text",
  "contact_section",
  "stats",
  "image_text",
  "feature_grid",
  "steps",
  "testimonial",
  "gallery",
] as const;
export type VisualCmsSectionType = (typeof VISUAL_CMS_SECTION_TYPES)[number];

export const VISUAL_CMS_VISIBILITY_DEVICES = ["all", "desktop", "tablet", "mobile"] as const;
export type VisualCmsVisibilityDevice = (typeof VISUAL_CMS_VISIBILITY_DEVICES)[number];

/** Row shape returned by get_visual_cms_pages (list — no sections). */
export interface VisualCmsPageListRow {
  id: string;
  slug: string;
  title: string;
  page_type: VisualCmsPageType;
  status: VisualCmsPageStatus;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: Json;
  og_image: string | null;
  is_active: boolean;
  version: number;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  created_by: string | null;
  updated_by: string | null;
}

export interface VisualCmsFaqItem {
  id: string;
  section_id: string;
  question: string;
  answer: string;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface VisualCmsCard {
  id: string;
  section_id: string;
  title: string;
  description: string | null;
  icon: string | null;
  image: string | null;
  button_text: string | null;
  button_link: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface VisualCmsSection {
  id: string;
  page_id: string;
  section_type: VisualCmsSectionType;
  section_key: string | null;
  sort_order: number;
  is_active: boolean;
  background_color: string | null;
  text_color: string | null;
  layout_variant: string | null;
  visibility_device: VisualCmsVisibilityDevice;
  content: Json;
  styles: Json;
  settings: Json;
  version: number;
  created_at: string;
  updated_at: string;
  faq_items: VisualCmsFaqItem[];
  cards: VisualCmsCard[];
}

/** Full payload returned by get_visual_cms_page_by_id / by_slug. */
export interface VisualCmsPageDetail extends VisualCmsPageListRow {
  sections: VisualCmsSection[];
}

export interface VisualCmsPagesListEnvelope {
  success: boolean;
  page?: number;
  limit?: number;
  total?: number;
  data?: VisualCmsPageListRow[];
  error?: string;
}

export interface VisualCmsPageDetailEnvelope {
  success: boolean;
  data?: VisualCmsPageDetail;
  error?: string;
}

export type VisualCmsManagePageAction =
  | "create"
  | "update"
  | "delete"
  | "restore"
  | "publish"
  | "unpublish"
  | "duplicate";

export type VisualCmsManageSectionAction = "create" | "update" | "delete" | "restore" | "reorder";

export type VisualCmsManageChildAction = "create" | "update" | "delete" | "reorder";

/** -------------------------------------------------------------------------
 * Section content shapes (the `content` jsonb column on visual_cms_sections).
 * Each section_type carries a different shape; readers/editors switch on
 * section_type. Optional fields default to "" / null in the renderer.
 * ----------------------------------------------------------------------- */

export interface VisualCmsHeroContent {
  title?: string;
  /**
   * Optional accent phrase rendered in the brand gradient after the main title.
   * Leave empty for a plain title with no highlight.
   */
  titleHighlight?: string;
  subtitle?: string;
  description?: string;
  imageUrl?: string;
  imageAlt?: string;
  ctaText?: string;
  ctaLink?: string;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
}

export interface VisualCmsFaqContent {
  title?: string;
  /**
   * Optional accent phrase rendered after the title in the brand gradient.
   * Mirrors the Hero section convention.
   */
  titleHighlight?: string;
  subtitle?: string;
  /** FAQ rows live in visual_cms_faq_items — relational; not duplicated here. */
}

export interface VisualCmsRichTextContent {
  html?: string;
}

export interface VisualCmsCardGridContent {
  title?: string;
  subtitle?: string;
  /** 2, 3, or 4; renderer defaults to 3. */
  columns?: number;
  /** Card rows live in visual_cms_cards — relational. */
}

export interface VisualCmsCtaBannerContent {
  title?: string;
  subtitle?: string;
  ctaText?: string;
  ctaLink?: string;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
}

/** Default empty content per section type — used when admin adds a new section. */
export function emptyContentForSectionType(t: VisualCmsSectionType): Record<string, unknown> {
  switch (t) {
    case "hero":
    case "faq":
    case "cta_banner":
      return {};
    case "rich_text":
      return { html: "" };
    case "card_grid":
    case "feature_grid":
    case "trust_blocks":
      return { columns: 3 };
    default:
      return {};
  }
}

/** -------------------------------------------------------------------------
 * Versioning (Phase 5)
 * ----------------------------------------------------------------------- */

export type VisualCmsVersionTrigger = "publish" | "restore" | "pre_restore" | "manual";

export interface VisualCmsPageVersionRow {
  id: string;
  page_id: string;
  version_number: number;
  trigger: VisualCmsVersionTrigger;
  note: string | null;
  created_by: string | null;
  created_at: string;
  preview: {
    section_count: number;
    title: string | null;
    status: string | null;
  };
}

export interface VisualCmsPageVersionDetail {
  id: string;
  page_id: string;
  version_number: number;
  trigger: VisualCmsVersionTrigger;
  note: string | null;
  created_by: string | null;
  created_at: string;
  snapshot: VisualCmsPageDetail;
}

export interface VisualCmsPageVersionsListEnvelope {
  success: boolean;
  page?: number;
  limit?: number;
  total?: number;
  data?: VisualCmsPageVersionRow[];
  error?: string;
}

export interface VisualCmsPageVersionDetailEnvelope {
  success: boolean;
  data?: VisualCmsPageVersionDetail;
  error?: string;
}

/** Human label for the "Add section" picker. */
export const VISUAL_CMS_SECTION_TYPE_LABELS: Record<VisualCmsSectionType, string> = {
  hero: "Hero",
  card_grid: "Card grid",
  faq: "FAQ",
  cta_banner: "CTA banner",
  timeline: "Timeline",
  trust_blocks: "Trust blocks",
  guidelines: "Guidelines",
  rich_text: "Rich text",
  contact_section: "Contact",
  stats: "Stats",
  image_text: "Image + text",
  feature_grid: "Feature grid",
  steps: "Steps",
  testimonial: "Testimonial",
  gallery: "Gallery",
};
